import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import BaseSwiper from '@src/components/swiper/base-swiper';
import MoCommonTop from '@src/ui-components/mo-common-top';

const styles = require('./index.less');

const SECTION_ANCHORS = {
  basic_info: { group: 'group12', content: 'group13', bottom: 'layer40', top: 'layer47', title: 'layer48' },
  journey: { group: 'group10', content: 'group11', bottom: 'layer31', top: 'layer38', title: 'layer39' },
  family: { group: 'group8', content: 'group9', bottom: 'layer22', top: 'layer29', title: 'layer30' },
  social: { group: 'group6', content: 'group7', bottom: 'layer13', top: 'layer20', title: 'layer21' },
  memory: { group: 'group4', content: 'group5', bottom: 'layer4', top: 'layer11', title: 'layer12' }
};

const DEFAULT_ANCHOR = SECTION_ANCHORS.basic_info;

function handleActionKeyDown(event, action) {
  if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
    event.preventDefault();
    action();
  }
}

// react-id-swiper 通过 cloneElement 向每个子元素注入 swiper-slide 类后再渲染；
// 函数组件若不透传 className，Swiper 会因找不到 slide 而无法换页。。
// 每页的行级入场动效由 .swiper-slide-active 类驱动（见 less），翻到哪页哪页逐行浮现。
function ReviewSection({ section, className }) {
  const anchor = SECTION_ANCHORS[section.id] || DEFAULT_ANCHOR;

  return (
    <div className={cx(styles[anchor.group], styles.reviewSlide, className)} data-role="review-slide">
      <div className={cx(styles[anchor.content], styles.sectionContent)}>
        {section.lines.map(line => (
          <div className={styles.reviewLine} key={line.id}>
            {line.segments.map((segment, segmentIndex) => (
              <span className={segment.type === 'dynamic' ? styles.dynamicSegment : undefined} key={`${line.id}-${segmentIndex}`}>
                {segment.text}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewSectionTitle({ section }) {
  if (!section) {
    return null;
  }

  const anchor = SECTION_ANCHORS[section.id] || DEFAULT_ANCHOR;
  // key 含拆页后缀：同章节跨页翻页时标题节点重建、入场特效重播，与换章节奏一致
  return (
    <div key={section.key || section.id} className={cx(styles[anchor.title], styles.titleSwapIn)} aria-label={section.title} data-role="review-section-title" />
  );
}

const MoVeteranReviewStages = ({
  className,
  phase,
  sections = [],
  currentIndex = 0,
  reviewStatus,
  swiperStore,
  completionPending = false,
  onStart,
  onSkip,
  onIndexChange,
  onReachEnd
}) => {
  const reachEndLockedRef = useRef(false);

  useEffect(
    () => {
      if (phase !== 'review' || !swiperStore || !swiperStore.swiperRef || !swiperStore.swiperRef.current) return undefined;
      const swiper = swiperStore.swiperRef.current.swiper;
      if (!swiper || typeof swiper.on !== 'function') return undefined;

      const handleSlideChange = () => onIndexChange(swiper.realIndex);
      const handleTouchEnd = () => {
        const isLastSection = sections.length > 0 && swiper.realIndex === sections.length - 1;
        if (swiper.isEnd && isLastSection && swiper.touches && swiper.touches.diff < 0 && !completionPending && !reachEndLockedRef.current) {
          reachEndLockedRef.current = true;
          const result = onReachEnd();
          const unlockReachEnd = () => {
            reachEndLockedRef.current = false;
          };
          Promise.resolve(result).then(unlockReachEnd, unlockReachEnd);
        }
      };

      swiper.on('slideChange', handleSlideChange);
      swiper.on('touchEnd', handleTouchEnd);
      return () => {
        if (typeof swiper.off === 'function') {
          swiper.off('slideChange', handleSlideChange);
          swiper.off('touchEnd', handleTouchEnd);
        }
      };
    },
    [completionPending, onIndexChange, onReachEnd, phase, sections.length, swiperStore]
  );

  const showReview = phase === 'review';
  const pageText = `${currentIndex + 1} / ${sections.length}`;

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.layer1} data-name="回顾阶段页-背景" />

      {/* 右上角跳过按钮仅首页保留（2026-09-22 需求）：review 阶段干掉关闭按钮，
          跳过能力改由页码左侧"跳过回顾"文字入口承接（同走 onSkip 跳组队页）；summary 已越过回顾，不渲染 */}
      {reviewStatus === 1 && phase === 'home' ? (
        <div
          className={cx(styles.layer49, styles.action)}
          data-name="跳过按钮"
          data-role="skip-review"
          onClick={onSkip}
          onKeyDown={event => handleActionKeyDown(event, onSkip)}
          role="button"
          tabIndex={0}
        />
      ) : null}

      {showReview && sections.length > 0 ? (
        <div className={styles.group1} data-name="回顾内容页">
          <div className={cx(styles.layer2, styles.scrollUnfurl)} data-name="回顾内容页-卷轴背景" />
          <div className={cx(styles.group2, styles.contentUnfurl)} data-name="回顾内容页-回顾内容">
            <div className={styles.swiperViewport}>
              {/* 标题层：不随翻页横滑，按当前章节切换并重播入场特效 */}
              <ReviewSectionTitle section={sections[currentIndex] || sections[0]} />
              {/* 滑动窗口收窄到上下分割线之间（75~1035）：翻页只发生在正文区 */}
              <div className={styles.swiperWindow}>
                {/* key 用 section.key：超长章节拆页后同 id 连续多页（如 memory/memory--p2），key 必须唯一 */}
                <BaseSwiper store={swiperStore}>{sections.map(section => <ReviewSection section={section} key={section.key || section.id} />)}</BaseSwiper>
              </div>
              {/* 固定分割线对：叠在滑动窗口上下缘，位置取首屏稿基准（上 75 / 下 1035） */}
              <div className={styles.fixedDividerTop} aria-hidden="true" />
              <div className={styles.fixedDividerBottom} aria-hidden="true" />
            </div>
            <div className={cx(styles.group3, styles.staggerBadge)} data-name="翻页角标">
              <div className={styles.layer3} data-name={`下滑翻页 ${pageText}`} data-text={`下滑翻页\n${pageText}`}>
                下滑翻页
                <br />
                {pageText}
              </div>
              {/* 跳过回顾入口（2026-09-22 需求）：review 阶段替代右上角关闭按钮承接跳过，
                  与首页跳过同走 onSkip 直达组队页（不再跳回顾总结）；仅已完成回顾用户可见，首次回顾必须看完全部内容 */}
              {reviewStatus === 1 ? (
                <div
                  className={cx(styles.skipToEndEntry, styles.action)}
                  data-name="跳过回顾"
                  data-role="skip-to-end"
                  onClick={onSkip}
                  onKeyDown={event => handleActionKeyDown(event, onSkip)}
                  role="button"
                  tabIndex={0}
                >
                  跳过回顾&gt;
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* 首页常驻渲染（不随 phase 卸载）：离开 home 时播上移淡出退场，被滑入的卷轴内容页覆盖；回 home 时类移除即恢复 */}
      <div className={cx(styles.group14, showReview ? styles.homeLeaving : undefined)} data-name="回顾首页">
        <div className={styles.layer50} data-name="首页slogan" />
        <div
          className={cx(styles.layer51, styles.action)}
          data-name="前往回顾按钮"
          data-role="start-review"
          onClick={onStart}
          onKeyDown={event => handleActionKeyDown(event, onStart)}
          role="button"
          tabIndex={0}
        />
        <div className={styles.group15} data-name="描述性文字">
          <div className={styles.layer52} data-name="《剑侠世界3》老玩家专属">
            《剑侠世界3》老玩家专属
          </div>
        </div>
      </div>

      <MoCommonTop />
    </div>
  );
};

export default MoVeteranReviewStages;
